"use strict";
/**
 * Edit body authority — primary user flow first.
 *
 * ## Happy path (design from the start, not mid-state)
 *
 * ```
 * 1. OPEN   Load note → row.edit.pnBody = server text
 *           Textarea paints that text (model → DOM).
 *           Draft baseline = that full body (not empty).
 *
 * 2. TYPE   User focuses textarea and edits.
 *           ng-model keeps row.edit.pnBody in step with keystrokes.
 *           While focused, the textarea DOM is truth (incl. clear-all).
 *
 * 3. ACT    User clicks Save / Map / Compress / Encrypt…
 *           Focus leaves the textarea first (browser order).
 *           Flush: take the text the user was editing, then act.
 * ```
 *
 * Branching only handles lag between steps 2 and 3 (DOM vs model),
 * not a separate “always prefer DOM” policy.
 *
 * ## Step 3 flush rules (after TYPE)
 *
 * | Situation | Meaning | Body to use |
 * |-----------|---------|-------------|
 * | DOM === model | In sync | Either |
 * | Focused on textarea | Still typing | DOM |
 * | Unfocused, DOM non-empty, ≠ model | Typed then clicked action | DOM |
 * | Unfocused, DOM empty, model non-empty | Still on OPEN (textarea not painted) — **not** “user cleared” | **model** |
 * | Unfocused, DOM empty, model empty | Empty note or intentional clear after blur | empty |
 *
 * ## Explicit replaces (not TYPE)
 *
 * Draft restore, compress, encrypt, decrypt, map structure write:
 * set model + textarea together (`external` / `map-write`). Never model-only.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.formatWithPreamble = exports.setEditBodyField = exports.planBodyApply = exports.shouldDisableOutlineMap = exports.bodyForSaveAfterOpenTypeAct = exports.afterTyping = exports.openRecordBody = exports.planDesyncHeal = exports.resolveAuthoritativeBody = void 0;
function asText(value) {
    return value == null ? '' : String(value);
}
/**
 * Body string for save / draft / crypto after OPEN → TYPE → ACT.
 * Implements the flush table in the file header.
 */
function resolveAuthoritativeBody(opts) {
    var model = asText(opts.model);
    if (!opts.hasTextarea) {
        return model;
    }
    var dom = asText(opts.dom);
    if (dom === model) {
        return model;
    }
    // Phase TYPE: user is in the box (including clear-all while focused).
    if (opts.textareaFocused) {
        return dom;
    }
    // Phase OPEN still: textarea not painted yet — do not treat as wipe.
    if (dom === '' && model !== '') {
        return model;
    }
    // Phase TYPE then ACT: user typed (DOM has text), then clicked Save/Map.
    return dom;
}
exports.resolveAuthoritativeBody = resolveAuthoritativeBody;
/**
 * Reconcile model and textarea after OPEN or before ACT.
 *
 * - OPEN incomplete: empty DOM + non-empty model → paint DOM from model.
 * - After TYPE: non-empty or focused DOM ahead → update model from DOM.
 */
function planDesyncHeal(model, dom, textareaFocused) {
    if (textareaFocused === void 0) { textareaFocused = false; }
    var resolved = resolveAuthoritativeBody({
        model: model,
        dom: dom,
        hasTextarea: true,
        textareaFocused: textareaFocused,
    });
    if (resolved === model && resolved !== dom) {
        return { nextModel: model, writeDomFromModel: true, phase: 'open' };
    }
    if (resolved !== model) {
        return {
            nextModel: resolved,
            writeDomFromModel: false,
            phase: textareaFocused ? 'type' : 'flush',
        };
    }
    return {
        nextModel: model,
        writeDomFromModel: false,
        phase: textareaFocused ? 'type' : 'open',
    };
}
exports.planDesyncHeal = planDesyncHeal;
/**
 * Scenario helpers for tests: primary flow only.
 * openRecord → typeInBox → flushForAction
 */
function openRecordBody(serverPnBody) {
    return asText(serverPnBody);
}
exports.openRecordBody = openRecordBody;
function afterTyping(opts) {
    return resolveAuthoritativeBody({
        model: opts.openedBody,
        dom: opts.typedDom,
        hasTextarea: true,
        textareaFocused: opts.stillFocused,
    });
}
exports.afterTyping = afterTyping;
/**
 * Full primary path: open → type → click action (unfocused flush).
 * `modelAfterTyping` is row.edit.pnBody if ng-model kept up; may lag last keystrokes.
 * `domAfterTyping` is always the textarea value at click time.
 */
function bodyForSaveAfterOpenTypeAct(opts) {
    // Prefer live flush inputs; openedFromServer only documents the journey.
    void opts.openedFromServer;
    return resolveAuthoritativeBody({
        model: opts.modelAtClick,
        dom: opts.domAtClick,
        hasTextarea: true,
        textareaFocused: false,
    });
}
exports.bodyForSaveAfterOpenTypeAct = bodyForSaveAfterOpenTypeAct;
/** Outline map is only useful on human plaintext. */
function shouldDisableOutlineMap(state) {
    if (!state)
        return false;
    if (state.isEncrypted && state.isLocked)
        return true;
    return false;
}
exports.shouldDisableOutlineMap = shouldDisableOutlineMap;
/**
 * Plan side-effects for applying a new body string.
 * Controllers use this so tests can assert notifications without Angular.
 */
function planBodyApply(kind, nextBody, encryptionState) {
    var body = asText(nextBody);
    return {
        body: body,
        flushOutlineFirst: kind === 'live-flush' || kind === 'external',
        notifyOutlineExternal: kind === 'external' || kind === 'map-write',
        disableOutlineMap: shouldDisableOutlineMap(encryptionState),
    };
}
exports.planBodyApply = planBodyApply;
function setEditBodyField(target, body) {
    var next = asText(body);
    if (target) {
        target.pnBody = next;
    }
    return next;
}
exports.setEditBodyField = setEditBodyField;
function formatWithPreamble(payload, caption) {
    var cap = caption != null ? String(caption).trim() : '';
    if (!cap)
        return payload;
    return "s1 > ".concat(cap, " > ").concat(payload);
}
exports.formatWithPreamble = formatWithPreamble;
